import type { ReactNode, VoidFunctionComponent } from 'react';

import { withTargetChecked } from '../../../utils';
import FormLabel from '../Forms/FormLabel';
import LabeledCheckbox from '../Forms/LabeledCheckbox';
import type { SelectOption, SelectOptionWithAttributes } from '../../../types';
import type { TooltipPlacement } from '../Tooltip';

import css from '../Forms/Checkbox.module.less';

type Props = {
  //  Label for the multiselect.
  label?: string;
  //  This is all of the checkboxes, with `label` used as the label and `value` as the key. Note you can use `selectOptionFactory` to create SelectOptions easily.
  options: SelectOptionWithAttributes[];
  //  Values are the values which are currently selected.
  values: SelectOption[];
  //  Change handler. Gets all selected options after the change takes effect.
  onChange: (values: SelectOption[]) => void;
  // The label for the parent checkbox
  parentCheckboxLabel: string;
  // Tooltip text for the parent checkbox
  parentTooltip?: string | ReactNode;
  //  If the checkboxes are disabled or not.
  isDisabled?: boolean;
  //  ID for testing purposes.
  testId?: string;
  tooltipPlacement?: TooltipPlacement;
};

/**
 * FilterCardNestedCheckboxes renders a parent checkbox with a set of nested checkboxes under it.
 * The component handles the logic for checking and unchecking the parent checkbox based on the child checkboxes.
 * They use the SelectOption shape, where `label` is the text label
 * rendered beside the checkbox, and `value` is a unique key for that checkbox.
 *
 * This is intended to be used as a child of FilterCard, however it could
 * probably also be used outside that context.
 */
const FilterCardNestedCheckboxes: VoidFunctionComponent<Props> = ({
  label,
  options,
  values,
  onChange,
  isDisabled,
  testId,
  parentCheckboxLabel,
  parentTooltip,
  tooltipPlacement,
}) => {
  const onParentCheckboxCheck = withTargetChecked(isChecked => {
    if (isChecked) {
      // By using a set, we can make sure we don't have duplicate values.
      const newValues = new Set(values);
      options.forEach((nestedOptions) => newValues.add(nestedOptions));
      onChange(Array.from(newValues));
    } else {
      const nestedOptionValues = new Set(options.map(({ value }) => value));
      onChange(values.filter(({ value }) => !nestedOptionValues.has(value)));
    }
  });

  const onCheckboxCheck = (option: SelectOption) => withTargetChecked(isChecked => {
    onChange(isChecked
      ? [...values, option] // Add to list
      : values.filter(({ value }) => value !== option.value)); // Remove from list
  });

  const getIsChecked = (value: string) => {
    return !!values.find(checkbox => checkbox.value === value);
  };

  return (
    <div className={css.checkboxes} data-test-id={testId}>
      {label && <FormLabel label={label} />}
      <div className={css.checkboxes}>
        <LabeledCheckbox
          key={parentCheckboxLabel}
          id={parentCheckboxLabel}
          isChecked={options.every((option) => values.some((value) => value.value === option.value))}
          label={parentCheckboxLabel}
          tooltip={parentTooltip}
          tooltipPlacement={tooltipPlacement}
          onChange={onParentCheckboxCheck}
          testId={testId ? `${testId}--${parentCheckboxLabel}` : undefined}
          isDisabled={isDisabled}
          isRippleDisabled
        />
        <div className={`${css.checkboxes} ${css['checkboxes-nested']}`}>
          {options.map(option =>
            <LabeledCheckbox
              key={option.value}
              id={option.value}
              isChecked={getIsChecked(option.value)}
              label={option.label}
              tooltip={option.tooltip}
              tooltipPlacement={tooltipPlacement}
              onChange={onCheckboxCheck(option)}
              testId={testId ? `${testId}--${option.value}` : undefined}
              isDisabled={isDisabled || option.isDisabled}
              isRippleDisabled
            />)}
        </div>
      </div>
    </div>
  );
};

export default FilterCardNestedCheckboxes;
